import type { MouseEvent } from 'react'
import { PRIVACY_PDF_URL, SUPPORT_PHONE_TEL } from '../config'
import type { BatteryOption, Chip, VoiceState } from '../state/types'
import { PhoneIcon } from './icons'

interface Props {
	state: VoiceState
	onChip: (chip: Chip) => void
	onOption: (opt: BatteryOption) => void
	onReceiptCall: (e: MouseEvent<HTMLAnchorElement>) => void
}

/** Layer 4b — the scrollable center: one assistant line (replaced per
    utterance), the last user answer in quotes, and exactly one transient
    content slot (chips | ✓ card | option cards | price | draft | receipt). */
export function Stage({ state, onChip, onOption, onReceiptCall }: Props) {
	return (
		<div className="am-stage">
			<div className="am-stage-col">
				{state.assistantLine && (
					<div className="am-assistant" key={`line-${state.lineId}`}>
						{state.assistantLine}
					</div>
				)}
				{state.userEcho && (
					<div className="am-echo-line" key={`echo-${state.echoId}`}>
						„{state.userEcho}”
					</div>
				)}

				{state.chips.length > 0 && (
					<div className="am-chips">
						{state.chips.map((c) => (
							<button
								type="button"
								key={c.id}
								className={`am-chip${c.primary ? ' primary' : ''}`}
								onClick={() => onChip(c)}
							>
								{c.label}
							</button>
						))}
					</div>
				)}

				{state.card && (
					<div className={`am-card${state.card.green ? ' green' : ''}${state.card.leaving ? ' leaving' : ''}`}>
						<span className="am-card-coin">✓</span>
						<div className="am-card-body">
							<div className="am-card-label">{state.card.label}</div>
							<div className="am-card-value">{state.card.value}</div>
							{state.card.note && (
								<div className={`am-card-note${state.card.noteGreen ? ' green' : ''}`}>{state.card.note}</div>
							)}
						</div>
					</div>
				)}

				{state.options && (
					<div className="am-options">
						{state.options.map((o) => (
							<button
								type="button"
								key={o.id}
								className={`am-option${o.tag || o.state === 'chosen' ? ' recommended' : ''}${o.state === 'dimmed' ? ' dimmed' : ''}`}
								onClick={() => onOption(o)}
							>
								<div className="am-option-inner">
									<img src={o.img} alt="" className="am-option-photo" />
									<div className="am-option-main">
										<div className="am-option-top">
											<span className="am-option-name">{o.name}</span>
											{o.tag && <span className="am-option-tag">{o.tag}</span>}
											<span className="am-option-top-spacer" />
											<span className="am-option-price">{o.price}</span>
										</div>
										<div className="am-option-spec">{o.spec}</div>
										<div className="am-option-reason">{o.reason}</div>
									</div>
								</div>
							</button>
						))}
					</div>
				)}

				{state.price && (
					<div className={`am-price${state.price.leaving ? ' leaving' : ''}`}>
						<div className="am-price-head">
							<span className="am-price-caption">Végösszeg</span>
							<span className="am-price-spacer" />
							<span className="am-price-total">{state.price.total}</span>
						</div>
						<div className="am-price-lines">
							{state.price.lines.map((l) => (
								<div className="am-price-line" key={l.k}>
									<span>{l.k}</span>
									<span>{l.v}</span>
								</div>
							))}
						</div>
						<div className="am-price-note">
							A végösszeg mindent tartalmaz — kiszállás, beszerelés, régi akku elszállítása.
						</div>
					</div>
				)}

				{state.draft && (
					<div className="am-draft">
						<div className="am-draft-text">
							Ha most hív minket, említse ezt a kódot — kollégánk mindent látni fog, amit eddig egyeztettünk:
						</div>
						<div className="am-draft-code">{state.draft.code}</div>
						<div className="am-draft-phone">Ügyfélszolgálat: {state.draft.phone}</div>
					</div>
				)}

				{state.success && (
					<div className="am-success">
						<div className="am-success-strip" />
						<span className="am-success-coin-ring">
							<span className="am-success-coin">✓</span>
						</span>
						<div className="am-success-title">Rendelését rögzítettük!</div>
						<div className="am-success-order">
							<span className="am-success-order-caption">Rendelésszám</span>
							<span className="am-success-order-no">{state.success.orderNo}</span>
						</div>
						<div className="am-success-arrival">
							Szerelőnk <b>{state.success.slot}</b> érkezik.
						</div>
						<div className="am-success-trust">
							Minden pontot közösen egyeztettünk — a részleteket fent ellenőrizheti.
						</div>
						<div className="am-success-cta-zone">
							<a className="am-success-cta" href={`tel:${SUPPORT_PHONE_TEL}`} onClick={onReceiptCall}>
								<PhoneIcon fill="#fff" />
								Ha bármi kérdése van: hívjon minket
							</a>
						</div>
						<div className="am-success-privacy">
							<a href={PRIVACY_PDF_URL} target="_blank" rel="noopener">
								Adatkezelési tájékoztató
							</a>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
